import Button from '@/components/Buttons/Button';
import IconButton from '@/components/Buttons/IconButton';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import { useDeleteExternalKey, useGetExternalKey, useUpsertExternalKey } from '@/hooks/external-keys';
import BackArrow from '@/images/BackArrow.svg';
import Close from '@/images/Close.svg';
import dump from '@/images/dump.svg';
import LockKeyhole from '@/images/LockKeyhole.svg';
import { validateElevenlabsKey } from '@/services/api';
import { useFlag } from '@/services/featureFlags';
import { ttsVoiceModal } from '@/state/presenters';
import theme from '@/theme';
import { StyleCollection } from '@/utils/types';
import { ExternalKeysProviders } from '@d-id/types';
import styled from '@emotion/styled';
import { Alert, DialogActions, DialogContent, TextField, Typography } from '@mui/material';
import { ChangeEvent, useEffect, useState } from 'react';
import { useSetRecoilState } from 'recoil';

const styles: StyleCollection = {
    dialogButton: {
        fontSize: '1.6rem',
        alignItems: 'flex-start',
    },
    fieldButton: {
        whiteSpace: 'nowrap',
        fontSize: '14px',
        padding: '4px 6px',
    },
    dialogActions: {
        justifyContent: 'start !important',
    },
    dialogTitle: {
        height: '5rem',
        fontSize: '1.6rem',
        padding: '8px',
    },
    rootStyle: {
        padding: '16px',
        borderRadius: '12px',
    },
};

const Container = styled.div`
    overflow: hidden;
    padding: 16px 0;
`;

const Header = styled.div`
    display: flex;
    width: 100%;
    align-items: center;
    padding: 0 1rem;
    height: 5rem;
    font-size: 2rem;

    h3 {
        padding: 0 8px;
    }

    svg {
        color: ${theme.colors.black.main};
    }

    button {
        width: 40px;
        height: 40px;
    }

    button:last-of-type {
        margin-left: auto;
    }
`;

const StyledAlert = styled(Alert)`
    &.MuiPaper-root.MuiAlert-standard {
        font-size: 16px;
        font-style: normal;
        line-height: normal;
        letter-spacing: -0.32px;
        width: 100%;
    }
`;

const StyledErrorAlert = styled(StyledAlert)`
    &.MuiPaper-root.MuiAlert-standard {
        margin: 12px 0 0;
        color: #e22020;
        padding: 2px 16px;
    }
`;

const StyledInfoAlert = styled(StyledAlert)`
    &.MuiPaper-root.MuiAlert-standard {
        margin: 12px 0;
        color: #1d1d1d;
        font-weight: 700;
        padding: 6px 16px;
        background-color: ${theme.colors.black[100]};
    }

    & .MuiAlert-icon {
        color: #1d1d1d;
    }
`;

const StyledH4 = styled(Typography)`
    color: #5b5958;
    align-items: center;
    display: flex;

    svg {
        margin-left: 4px;
    }
`;

const ProvidersContainer = styled.div`
    display: flex;
    gap: 16px;
    margin: 16px 0;
`;

const StyledTextField = styled(TextField)`
    margin: 16px 0;

    & .MuiOutlinedInput-root {
        & fieldset {
        }
        &:hover fieldset {
            border: 1px solid ${({ theme }) => theme.colors.secondary.main};
        }
        &.Mui-focused fieldset {
            border: 1px solid ${({ theme }) => theme.colors.secondary.main};
        }
    }

    p {
        font-size: 14px;
        margin: 4px 0;
    }
`;

const TextFieldButtonsContainer = styled.div`
    display: flex;
    gap: 12px;
`;

const TransparentButton = styled(Button)`
    background-color: transparent;
    border: 1px solid ${theme.colors.secondary[900]};

    &.MuiButtonBase-root:hover {
        background-color: #f3f3f3 !important;
    }

    &.MuiButtonBase-root:disabled {
        background-color: transparent !important;
    }
`;

const ProviderButton = styled(TransparentButton)<{ active?: boolean }>`
    padding: 32px;
    padding-top: 36px;
    padding-bottom: 36px;
    font-weight: ${({ active }) => (active ? '900' : '700')};
`;

interface IProps {
    setMode: (mode: 'select' | 'import' | 'clone-voice') => void;
}

function ImportVoiceContent({ setMode }: IProps) {
    const creditsToMinutesEnabled = useFlag('credits_to_minutes_enabled', false);

    const { data: externalKey } = useGetExternalKey(ExternalKeysProviders.Elevenlabs);
    const { mutateAsync: upsertExternalKey, isLoading: upsertLoading } = useUpsertExternalKey();
    const { mutateAsync: deleteExternalKey, isLoading: deleteLoading } = useDeleteExternalKey();

    const [provider] = useState<ExternalKeysProviders>(ExternalKeysProviders.Elevenlabs);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [validationError, setValidationError] = useState(false);
    const [secret, setSecret] = useState(externalKey?.sanitized_secret || '');

    const setVoiceModalState = useSetRecoilState(ttsVoiceModal);

    useEffect(() => {
        if (externalKey?.sanitized_secret && !secret) {
            setSecret(externalKey.sanitized_secret);
        }
    }, [externalKey?.sanitized_secret, secret]);

    const handleChange = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        setValidationError(false);
        setSecret(e.target.value);
    };

    const handleDelete = () => {
        setConfirmDelete(true);
    };

    const handleCancelDelete = () => {
        setConfirmDelete(false);
    };

    const handleConfirmDelete = async () => {
        await deleteExternalKey(provider);
        handleCancelDelete();
        setSecret('');
    };

    const handleConfirm = async () => {
        if (provider === ExternalKeysProviders.Elevenlabs) {
            try {
                await validateElevenlabsKey(secret);
            } catch (error) {
                setValidationError(true);
                return;
            }
        }

        const result = await upsertExternalKey({ provider, secret });
        setSecret(result.sanitized_secret);
        setMode('select');
    };

    const handleBack = () => {
        setMode('select');
    };

    const handleClose = () => {
        setVoiceModalState(false);
    };

    return (
        <Container>
            <Header>
                <IconButton ariaLabel="back" onClick={handleBack}>
                    <MaskedIcon src={BackArrow} size="24px" />
                </IconButton>
                <Typography variant="h3">Import voice</Typography>
                <IconButton ariaLabel="close" onClick={handleClose}>
                    <MaskedIcon src={Close} size="16px" />
                </IconButton>
            </Header>

            <DialogContent>
                <StyledInfoAlert severity="info">
                    When you use a 3rd party voice, it will consume your {creditsToMinutesEnabled ? 'minutes' : 'credits'} with that service
                    provider
                </StyledInfoAlert>

                <StyledH4 variant="h4">Service provider</StyledH4>
                <ProvidersContainer>
                    <ProviderButton active={provider === ExternalKeysProviders.Elevenlabs}>IIElevenLabs</ProviderButton>
                    <ProviderButton disabled>Coming soon</ProviderButton>
                </ProvidersContainer>

                <StyledH4 variant="h4">
                    API key
                    <MaskedIcon src={LockKeyhole} size="16px" />
                </StyledH4>

                {confirmDelete && (
                    <StyledErrorAlert severity="error" icon={false}>
                        <strong>Warning: </strong> This will disable access to your 3rd party voices.
                    </StyledErrorAlert>
                )}
                <StyledTextField
                    error={validationError}
                    helperText={validationError && 'Invalid API key'}
                    placeholder="Paste your API key here"
                    onChange={handleChange}
                    value={secret}
                    InputProps={{
                        endAdornment: (
                            <>
                                {confirmDelete ? (
                                    <TextFieldButtonsContainer>
                                        <TransparentButton onClick={handleCancelDelete} variant="contained" sx={styles.fieldButton}>
                                            Cancel
                                        </TransparentButton>
                                        <Button
                                            onClick={handleConfirmDelete}
                                            variant="contained"
                                            color="error"
                                            sx={styles.fieldButton}
                                            isLoading={deleteLoading}
                                            loadingText="Removing...">
                                            Remove key
                                        </Button>
                                    </TextFieldButtonsContainer>
                                ) : externalKey?.sanitized_secret ? (
                                    <IconButton ariaLabel="delete" onClick={handleDelete}>
                                        <MaskedIcon src={dump} size="16px" />
                                    </IconButton>
                                ) : null}
                            </>
                        ),
                    }}
                />
            </DialogContent>
            <DialogActions sx={styles.dialogActions}>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    sx={styles.dialogButton}
                    isLoading={upsertLoading}
                    loadingText="Saving..."
                    disabled={!secret || /^•*$/.test(secret)}>
                    Confirm
                </Button>
                <TransparentButton onClick={handleClose} variant="contained" sx={styles.dialogButton}>
                    Cancel
                </TransparentButton>
            </DialogActions>
        </Container>
    );
}

export default ImportVoiceContent;
